/**
 * hooks/useStomp.js — Phase 3
 *
 * Fix from initial draft:
 *  ✓ WS_BASE uses URL API to safely strip the /api path suffix
 *    (simple .replace('/api','') would corrupt URLs like http://api.company.com/api)
 *
 * Install:
 *   npm install @stomp/stompjs sockjs-client
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { tokenStore } from '../services/api';

// The WebSocket URL must include the Spring context-path (/api).
// We do NOT strip /api here — the SockJS endpoint is registered UNDER the context-path,
// so the full URL is:  http://localhost:8080/api/ws  (context-path + addEndpoint("/ws"))
function getWsBase() {
  const apiBase = process.env.REACT_APP_API_URL || '/api';
  // Then the SockJS URL becomes: /api/ws  (relative — setupProxy.js handles it)
  // Remove trailing slash only — keep the full path including /api
  return apiBase.replace(/\/$/, '');
}

const WS_BASE = getWsBase();
// e.g. 'http://localhost:8080/api'       → 'http://localhost:8080/api'
//      'https://api.company.com/api'     → 'https://api.company.com/api'
// Combined with endpoint '/ws' this gives: http://localhost:8080/api/ws  ✓

/**
 * @param {object}   options
 * @param {string}   options.endpoint   SockJS path, default '/ws'
 * @param {string[]} options.topics     Topics to subscribe to on connect
 * @param {function} options.onMessage  (destination, parsedBody) => void
 * @param {function} options.onError    (errorMessage) => void
 * @returns {{ status, publish, subscribe, unsubscribe }}
 */
export function useStomp({
  endpoint  = '/ws',
  topics    = [],
  onMessage = null,
  onError   = null,
} = {}) {
  const [status, setStatus] = useState('disconnected');

  const clientRef      = useRef(null);
  const subsRef        = useRef(new Map());
  const pendingSubsRef = useRef(new Map()); // topics queued before STOMP connects
  const onMsgRef  = useRef(onMessage);
  const onErrRef  = useRef(onError);
  const topicsRef = useRef(topics);

  useEffect(() => { onMsgRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onErrRef.current = onError;   }, [onError]);
  useEffect(() => { topicsRef.current = topics;   }, [topics]);

  const handleMessage = useCallback((destination, stompMsg) => {
    if (!onMsgRef.current) return;
    try {
      const body = stompMsg.body ? JSON.parse(stompMsg.body) : null;
      onMsgRef.current(destination, body);
    } catch {
      onMsgRef.current(destination, stompMsg.body);
    }
  }, []);

  const subscribeToTopic = useCallback((client, topic, cb) => {
    if (subsRef.current.has(topic)) return;
    const sub = client.subscribe(topic, (msg) => {
      handleMessage(topic, msg);
      if (cb) { try { cb(JSON.parse(msg.body)); } catch { cb(msg.body); } }
    });
    subsRef.current.set(topic, sub);
  }, [handleMessage]);

  useEffect(() => {
    const client = new Client({
      /*webSocketFactory: () => new SockJS(`${WS_BASE}${endpoint}`),*/
      webSocketFactory: () => new SockJS(`${WS_BASE}${endpoint}`, null, {
            transports: ['websocket', 'xhr-streaming', 'xhr-polling']  // skip eventsource
            }),
      // connectHeaders MUST be a plain object — @stomp/stompjs v6 does NOT accept a function.
      // Use beforeConnect to inject a fresh JWT before each (re)connect attempt.
      connectHeaders: {},
      beforeConnect: () => {
        const token = tokenStore.getAccess();
        client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      },

      heartbeatIncoming: 25_000,
      heartbeatOutgoing: 25_000,
      reconnectDelay:     5_000,

      onConnect: () => {
        setStatus('connected');
        // Subscribe to static topics declared at hook init
        topicsRef.current.forEach((t) => subscribeToTopic(client, t));
        // Flush any dynamic topics queued before connection was ready
        pendingSubsRef.current.forEach((cb, t) => subscribeToTopic(client, t, cb));
        pendingSubsRef.current.clear();
      },
      onDisconnect: () => {
        setStatus('disconnected');
        subsRef.current.clear();
        pendingSubsRef.current.clear();
      },
      onStompError: (frame) => {
        setStatus('error');
        onErrRef.current?.(frame.headers?.message ?? 'STOMP error');
      },
      onWebSocketError: () => {
        setStatus('error');
        onErrRef.current?.('WebSocket connection failed');
      },
    });

    setStatus('connecting');
    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
      clientRef.current = null;
      subsRef.current.clear();
      pendingSubsRef.current.clear();
    };
    // eslint-disable-next-line
  }, [endpoint]);

  const publish = useCallback((destination, body) => {
    const client = clientRef.current;
    if (!client?.connected) {
      console.warn('[STOMP] Not connected — message dropped:', destination);
      return;
    }
    client.publish({ destination, body: JSON.stringify(body) });
  }, []);

  const subscribe = useCallback((topic, cb) => {
    const client = clientRef.current;
    if (client?.connected) {
      subscribeToTopic(client, topic, cb);
    } else {
      // Not yet connected — queue for flush inside onConnect
      pendingSubsRef.current.set(topic, cb ?? null);
    }
  }, [subscribeToTopic]);

  const unsubscribe = useCallback((topic) => {
    const sub = subsRef.current.get(topic);
    sub?.unsubscribe();
    subsRef.current.delete(topic);
    pendingSubsRef.current.delete(topic); // also remove from pending if it never connected
  }, []);

  return { status, publish, subscribe, unsubscribe };
}

export default useStomp;
